import React, { useReducer, useCallback, useEffect, useMemo } from 'react'
import { getUser } from '@fs/zion-user'
import globalContext from './globalContext'
import globalReducer from './globalReducer'

const GlobalState = ({ children }) => {
  const accountInfoCache = JSON.parse(sessionStorage.getItem('fsDevPortalAccountInfo'))
  const applicationsCache = new Map(JSON.parse(sessionStorage.getItem('fsDevPortalApplications')))
  const adminDeveloperDataCache = JSON.parse(sessionStorage.getItem('fsDevPortalAdminData'))

  const initialState = {
    accountInfo: accountInfoCache ?? {
      developerId: null,
      name: '',
      title: '',
      email: '',
      receiveUpdates: false,
      companyName: '', // This maps to 'account.name' on the back end.
      sfContactId: '',
      account: {
        sfAccountId: '',
        name: '',
        ein: '',
      },
    },
    applications: applicationsCache ?? new Map(),
    adminDeveloperData: adminDeveloperDataCache ?? {},
    hasFetchedApplications: false,
    fetchApplicationsAgain: false,
  }

  const [state, dispatch] = useReducer(globalReducer, initialState)

  // Clear sessionStorage if a different user logs in
  useEffect(() => {
    const verifyCurrentUser = async () => {
      try {
        const { cisId } = await getUser()
        const cachedCisId = sessionStorage.getItem('fsDevPortalCisId')

        if (cachedCisId && cachedCisId !== cisId) {
          // Different user logged in - clear all cached data
          sessionStorage.removeItem('fsDevPortalAccountInfo')
          sessionStorage.removeItem('fsDevPortalApplications')
          sessionStorage.removeItem('fsDevPortalCisId')

          // Reset state to initial values
          dispatch({
            type: 'clear_session_cache',
          })
        } else if (!cachedCisId && cisId) {
          // First login - store the cisId
          sessionStorage.setItem('fsDevPortalCisId', cisId)
        }
      } catch (error) {
        console.error('Error verifying current user:', error)
      }
    }

    verifyCurrentUser()
  }, [])

  // (POST) Save the account infomation to the API when the user goes through the onboarding process. Make sure to include the FamilySearch user id and other information.
  const updateAccountInfo = useCallback((data) => {
    dispatch({
      type: 'update_account_info',
      payload: data,
    })
  }, [])

  // (GET) Store all applications from the API when the app loads.
  const setAllApplications = useCallback((data) => {
    dispatch({
      type: 'set_all_applications',
      payload: data,
    })
  }, [])

  // (POST) Add a new application to the session storage array
  const addNewApplication = useCallback((formData, id) => {
    dispatch({
      type: 'add_new_application',
      payload: formData,
      id,
    })
  }, [])

  // (POST/PUT) Update an existing application in the session storage array
  const updateApplication = useCallback(
    (formData, id) => {
      const updatedApplications = new Map(state.applications)
      updatedApplications.set(`${id}`, formData)
      dispatch({
        type: 'update_application',
        payload: updatedApplications,
        id,
      })
    },
    [state.applications]
  )

  // (DELETE) Remove an application from session storage array
  const deleteApplication = useCallback(
    (id) => {
      const newMap = new Map(state.applications)
      newMap.delete(`${id}`)
      dispatch({
        type: 'delete_application',
        payload: newMap,
      })
    },
    [state.applications]
  )

  // (POST/PUT) Add a new beta redirect to the application environment and update the session storage API
  const addNewRedirect = useCallback(
    (id, redirect, env) => {
      const newMap = new Map(state.applications)
      const application = newMap.get(`${id}`)
      const redirectKey = `${env}RedirectUrls`

      // Initialize the array if it doesn't exist or is null
      if (!application[redirectKey]) {
        application[redirectKey] = []
      }

      application[redirectKey].push(redirect)
      dispatch({
        type: 'add_new_redirect',
        payload: newMap,
      })
    },
    [state.applications]
  )

  // (POST/PUT) Update an exisiting redirect in the application environment and update the session storage API
  const updateRedirect = useCallback(
    (applicationId, index, redirect, env) => {
      const newMap = new Map(state.applications)
      newMap.get(`${applicationId}`)[`${env}RedirectUrls`].splice(index, 1, redirect)
      dispatch({
        type: 'update_redirect',
        payload: newMap,
      })
    },
    [state.applications]
  )

  // (DELETE) Remove a redirect from the application environment and update the API
  const deleteRedirect = useCallback(
    (applicationId, index, env) => {
      const newMap = new Map(state.applications)
      newMap.get(`${applicationId}`)[`${env}RedirectUrls`].splice(index, 1)
      dispatch({
        type: 'delete_redirect',
        payload: newMap,
      })
    },
    [state.applications]
  )

  const clearAccountInfoCache = useCallback(() => {
    dispatch({
      type: 'clear_account_info_cache',
    })
  }, [])

  const setHasFetchedApplications = useCallback((value) => {
    dispatch({
      type: 'set_has_fetched_applications',
      payload: value,
    })
  }, [])

  const resetFetchApplicationsAgain = useCallback(() => {
    dispatch({
      type: 'reset_fetch_applications_again',
    })
  }, [])

  const setAdminDeveloperData = useCallback((data) => {
    dispatch({
      type: 'set_admin_developer_data',
      payload: data,
    })
  }, [])

  return (
    <globalContext.Provider
      value={useMemo(
        () => ({
          accountInfo: state.accountInfo,
          applications: state.applications,
          adminDeveloperData: state.adminDeveloperData,
          setAdminDeveloperData,
          updateAccountInfo,
          addNewApplication,
          updateApplication,
          deleteApplication,
          addNewRedirect,
          updateRedirect,
          deleteRedirect,
          setAllApplications,
          clearAccountInfoCache,
          fetchApplicationsAgain: state.fetchApplicationsAgain,
          hasFetchedApplications: state.hasFetchedApplications,
          setHasFetchedApplications,
          resetFetchApplicationsAgain,
        }),
        [
          state,
          updateAccountInfo,
          addNewApplication,
          updateApplication,
          deleteApplication,
          addNewRedirect,
          updateRedirect,
          deleteRedirect,
          setAllApplications,
          clearAccountInfoCache,
          setHasFetchedApplications,
          resetFetchApplicationsAgain,
          setAdminDeveloperData,
        ]
      )}
    >
      {children}
    </globalContext.Provider>
  )
}

export default GlobalState
